import {
  CloseOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Button, Divider, Drawer, Grid, Spin, Tooltip } from 'antd';
import classNames from 'classnames';
import { ASSIGN_TOOL_ID } from 'infrastructure/consts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useUser from 'utils/useUser';

import ReturnTaskModal from './components/ReturnTaskModal';
import TaskFormModal from './components/TaskFormModal';
import { useAssignTask } from './hooks/use-assign-task';
import { useCompleteTask } from './hooks/use-complete-task';
import { useCreateTask } from './hooks/use-create-task';
import { useUnassignTask } from './hooks/use-unassign-task';
import { useUpdateTask } from './hooks/use-update-task';
import TaskItem from './TaskItem';
import { useGetTasksByPatient } from './hooks/use-get-tasks-by-patient';
import s from './styles.module.scss';
import {
  getOwnershipGroup,
  sortCompletedTasks,
  sortPendingTasks,
} from './utils';
import { canEditTask } from './types';

import type { FC } from 'react';
import type { ITaskCard, ITaskFormValues } from './types';

type ButtonRef = HTMLButtonElement & { nativeElement?: HTMLElement | null };

export interface ITasksDrawerProps {
  patientGuid: string;
  patientName?: string;
}

export const TasksDrawer: FC<ITasksDrawerProps> = ({
  patientGuid,
  patientName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'completed'>(
    'pending',
  );
  const [hasAssignToolNearby, setHasAssignToolNearby] = useState(false);
  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ITaskCard | null>(null);
  const [returningTask, setReturningTask] = useState<ITaskCard | null>(null);
  const [completingTaskGuid, setCompletingTaskGuid] = useState<string | null>(
    null,
  );
  const [assigningGuids, setAssigningGuids] = useState<Set<string>>(new Set());
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelTitleRef = useRef<HTMLParagraphElement>(null);
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const editTriggerRef = useRef<HTMLElement | null>(null);
  const returnTriggerRef = useRef<HTMLElement | null>(null);
  const pendingTabRef = useRef<ButtonRef>(null);
  const completedTabRef = useRef<ButtonRef>(null);
  const isMountedRef = useRef(true);

  const { t } = useTranslation();
  const { loginGuid } = useUser();
  const screens = Grid.useBreakpoint();
  const { tasks, summary, isLoading, isError } =
    useGetTasksByPatient(patientGuid);
  const assignTask = useAssignTask(patientGuid);
  const completeTask = useCompleteTask(patientGuid);
  const unassignTask = useUnassignTask(patientGuid);
  const createTask = useCreateTask(patientGuid);
  const updateTask = useUpdateTask(patientGuid);

  const handleCreateSubmit = useCallback(
    (values: ITaskFormValues) => {
      createTask.mutate(values, {
        onSuccess: () => {
          setIsCreateModalOpen(false);
          if (isMountedRef.current) createButtonRef.current?.focus();
        },
      });
    },
    [createTask],
  );

  const handleEditSubmit = useCallback(
    (values: ITaskFormValues) => {
      if (!editingTask) return;
      const { title, priority, description, taskType } = values;
      updateTask.mutate(
        {
          taskGuid: editingTask.guid,
          payload: { title, priority, description, taskType },
        },
        {
          onSuccess: () => {
            setEditingTask(null);
            if (isMountedRef.current) editTriggerRef.current?.focus();
          },
        },
      );
    },
    [editingTask, updateTask],
  );

  const handleCreateCancel = useCallback(() => {
    setIsCreateModalOpen(false);
    createButtonRef.current?.focus();
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingTask(null);
    editTriggerRef.current?.focus();
  }, []);

  const handleReturnClose = useCallback(() => {
    setReturningTask(null);
    returnTriggerRef.current?.focus();
  }, []);

  const handleReturnSubmit = useCallback(
    (returnNote: string) => {
      if (!returningTask) return;
      unassignTask.mutate(
        { taskGuid: returningTask.guid, returnNote },
        { onSuccess: handleReturnClose },
      );
    },
    [returningTask, unassignTask, handleReturnClose],
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setRootElement(document.getElementById('root'));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const getPendingEl = () =>
      pendingTabRef.current?.nativeElement ?? pendingTabRef.current;
    const getCompletedEl = () =>
      completedTabRef.current?.nativeElement ?? completedTabRef.current;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable
      )
        return;

      const pendingEl = getPendingEl();
      const completedEl = getCompletedEl();

      if (e.key === 'ArrowLeft' || e.key === 'Home') {
        e.preventDefault();
        setActiveTab('pending');
        pendingEl?.focus();
      } else if (e.key === 'ArrowRight' || e.key === 'End') {
        e.preventDefault();
        setActiveTab('completed');
        completedEl?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!rootElement) return;
    const hasAssignTool = !!rootElement.querySelector(`#${ASSIGN_TOOL_ID}`);
    setHasAssignToolNearby(hasAssignTool);
  }, [rootElement, isOpen]);

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (open) {
      const el =
        (createButtonRef.current as ButtonRef)?.nativeElement ??
        createButtonRef.current;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- focusVisible is specced in FocusOptions but not yet in TS lib.dom.d.ts
      el?.focus({ focusVisible: true } as any);
    } else {
      triggerRef.current?.focus();
    }
  }, []);

  const pendingCount = summary?.pendingCount ?? 0;
  const completedCount = summary?.completedCount ?? 0;

  const sortedPendingTasks = useMemo(
    () => sortPendingTasks(tasks?.pending ?? [], loginGuid ?? ''),
    [tasks, loginGuid],
  );

  const sortedCompletedTasks = useMemo(
    () => sortCompletedTasks(tasks?.completed ?? []),
    [tasks],
  );

  const displayedTasks =
    activeTab === 'pending' ? sortedPendingTasks : sortedCompletedTasks;
  const isMobile = !screens.md;
  const drawerWidth = isMobile ? '100%' : 500;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={s['empty-state']}>
          <Spin />
        </div>
      );
    }
    if (isError) {
      return (
        <div className={s['empty-state']}>
          <p>{t('tasksDrawer.errorLoading')}</p>
        </div>
      );
    }
    if (displayedTasks.length === 0) {
      return (
        <div className={s['empty-state']}>
          {activeTab === 'pending' ? (
            <>
              <p>{t('tasksDrawer.noPendingTasks')}</p>
              <p>{t('tasksDrawer.noPendingTasksBody')}</p>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setIsCreateModalOpen(true)}
                data-cy="create-task-empty-state-button"
              >
                {t('tasksDrawer.createTask')}
              </Button>
            </>
          ) : (
            <p>{t('tasksDrawer.noCompletedTasks')}</p>
          )}
        </div>
      );
    }
    return displayedTasks.map((task, index) => {
      const nextTask = displayedTasks[index + 1];
      const isGroupBoundary =
        activeTab === 'pending' &&
        nextTask &&
        getOwnershipGroup(task, loginGuid ?? '') !==
          getOwnershipGroup(nextTask, loginGuid ?? '');

      return (
        <div key={task.guid}>
          <TaskItem
            task={task}
            currentUserGuid={loginGuid ?? ''}
            onAssign={
              task.status === 'unassigned'
                ? () => {
                    setAssigningGuids((prev) => new Set(prev).add(task.guid));
                    assignTask.mutate(task.guid, {
                      onSettled: () =>
                        setAssigningGuids((prev) => {
                          const next = new Set(prev);
                          next.delete(task.guid);
                          return next;
                        }),
                    });
                  }
                : undefined
            }
            isAssigning={assigningGuids.has(task.guid)}
            isCompleting={completingTaskGuid === task.guid}
            onComplete={
              task.status === 'assigned' && task.assignedToGuid === loginGuid
                ? () => {
                    setCompletingTaskGuid(task.guid);
                    completeTask.mutate(task.guid, {
                      onSettled: () => setCompletingTaskGuid(null),
                    });
                  }
                : undefined
            }
            onReturn={
              task.status === 'assigned' && task.assignedToGuid === loginGuid
                ? () => {
                    returnTriggerRef.current =
                      document.activeElement instanceof HTMLElement
                        ? document.activeElement
                        : null;
                    setReturningTask(task);
                  }
                : undefined
            }
            onEdit={
              canEditTask(task, loginGuid ?? '')
                ? () => {
                    editTriggerRef.current =
                      document.activeElement instanceof HTMLElement
                        ? document.activeElement
                        : null;
                    setEditingTask(task);
                  }
                : undefined
            }
          />
          {index < displayedTasks.length - 1 && (
            <Divider
              className={isGroupBoundary ? s['divider-group'] : s.divider}
            />
          )}
        </div>
      );
    });
  };

  return (
    <>
      {rootElement &&
        createPortal(
          <button
            ref={triggerRef}
            className={classNames(s['tasks-trigger'], s.standalone)}
            data-move-down={hasAssignToolNearby ? 'true' : undefined}
            onClick={() => setIsOpen((prev) => !prev)}
            aria-label={t('tasksDrawer.triggerAriaLabel', {
              count: pendingCount,
            })}
            aria-expanded={isOpen}
            data-cy="open-tasks-drawer-button"
          >
            <div className={s['trigger-content']}>
              <span className={s['trigger-icon']}>
                <UnorderedListOutlined />
              </span>
              <span>
                {t('tasksDrawer.triggerLabel', { count: pendingCount })}
              </span>
            </div>
          </button>,
          rootElement,
        )}

      <Drawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        width={drawerWidth}
        closable={false}
        keyboard
        zIndex={1010}
        className={s.drawer}
        afterOpenChange={handleDrawerOpenChange}
      >
        <div className={s['panel-header']}>
          <p ref={panelTitleRef} className={s['panel-title']} tabIndex={-1}>
            {t('tasksDrawer.title')}
          </p>
          <div className={s['header-actions']}>
            <Button
              ref={createButtonRef}
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalOpen(true)}
              data-cy="create-task-button"
            >
              {t('tasksDrawer.createTask')}
            </Button>
            <Tooltip
              title={t('tasksDrawer.close')}
              trigger={['hover', 'focus']}
              placement="top"
            >
              <Button
                type="text"
                className={s['close-button']}
                icon={<CloseOutlined />}
                onClick={() => setIsOpen(false)}
                aria-label={t('tasksDrawer.close')}
                data-cy="close-tasks-drawer-button"
              />
            </Tooltip>
          </div>
        </div>

        <div
          className={s['filter-row']}
          role="tablist"
          aria-label={t('tasksDrawer.viewToggleAriaLabel')}
          data-cy="task-drawer-view-toggle"
        >
          <Button
            ref={pendingTabRef}
            id="pending-tab"
            type={activeTab === 'pending' ? 'primary' : 'default'}
            className={
              activeTab === 'pending' ? s['filter-button-active'] : undefined
            }
            onClick={() => setActiveTab('pending')}
            size="small"
            role="tab"
            aria-selected={activeTab === 'pending'}
            aria-controls={activeTab === 'pending' ? 'tasks-panel' : undefined}
            data-cy="pending-tasks-tab"
          >
            {t('tasksDrawer.pendingTasks', { count: pendingCount })}
          </Button>
          <Button
            ref={completedTabRef}
            id="completed-tab"
            type={activeTab === 'completed' ? 'primary' : 'default'}
            className={
              activeTab === 'completed' ? s['filter-button-active'] : undefined
            }
            onClick={() => setActiveTab('completed')}
            size="small"
            role="tab"
            aria-selected={activeTab === 'completed'}
            aria-controls={
              activeTab === 'completed' ? 'tasks-panel' : undefined
            }
            data-cy="completed-tasks-tab"
          >
            {t('tasksDrawer.completed', { count: completedCount })}
          </Button>
        </div>

        <div
          id="tasks-panel"
          className={s['tasks-list']}
          role="tabpanel"
          aria-labelledby={
            activeTab === 'pending' ? 'pending-tab' : 'completed-tab'
          }
        >
          {renderContent()}
        </div>
      </Drawer>

      <TaskFormModal
        open={isCreateModalOpen}
        mode="create"
        patientName={patientName}
        isSubmitting={createTask.isLoading}
        onSubmit={handleCreateSubmit}
        onCancel={handleCreateCancel}
      />

      <TaskFormModal
        open={!!editingTask}
        mode="edit"
        patientName={patientName}
        task={editingTask}
        isSubmitting={updateTask.isLoading}
        onSubmit={handleEditSubmit}
        onCancel={handleEditCancel}
      />

      <ReturnTaskModal
        open={!!returningTask}
        isSubmitting={unassignTask.isLoading}
        onSubmit={handleReturnSubmit}
        onCancel={handleReturnClose}
      />
    </>
  );
};
